import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
// import apiClient from '../utils/apiClient';
import LanguageSelector from './LanguageSelector';
import logo from '../assets/nodemented-logo.png';
import '../styles/auth.css';

import apiClient from '../utils/apiClient';
//TODO add retrieve password reset and email verification tokens


const AuthApp = ({ onLoginSuccess, user }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState('login');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    organizationName: '',
    accountType: 'user'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const API_BASE_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    if (!user) {
      document.body.classList.add('auth-body');
    } else {
      document.body.classList.remove('auth-body');
    }

    return () => {
      document.body.classList.remove('auth-body');
    };
  }, [user]);

  // Add password policy validation
  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    if (
      password.length < minLength ||
      !hasUpperCase ||
      !hasLowerCase ||
      !hasNumber ||
      !hasSpecialChar
    ) {
      return t('auth.passwordValidation');
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (mode === 'login') {
        const response = await apiClient.post('/auth/login', {
          email: formData.email,
          password: formData.password
        });
        const { user } = response.data; // No more need for token on client side
        onLoginSuccess(user, null); // No token to store
      } else {
        // Client-side validation
        if (formData.password !== formData.confirmPassword) {
          setError(t('auth.errors.passwordMismatch'));
          setLoading(false);
          return;
        }

        const passwordError = validatePassword(formData.password);
        if (passwordError) {
          setError(passwordError);
          setLoading(false);
          return;
        }

        if (formData.accountType === 'org-admin' && !formData.organizationName) {
          setError(t('auth.errors.organizationRequired'));
          setLoading(false);
          return;
        }

        // Inscription
        const response = await apiClient.post('/auth/register', {
          email: formData.email,
          password: formData.password,
          confirmPassword: formData.confirmPassword,
          firstName: formData.firstName,
          lastName: formData.lastName,
          organizationName: formData.accountType === 'org-admin' ? formData.organizationName : null,
          role: formData.accountType === 'org-admin' ? 'org-admin' : 'user',
          inviteCode: formData.inviteCode || null
        });
        
        // ✅ Use returned token for automatic login
        const { user, token } = response.data;
        onLoginSuccess(user, token);
      }
    } catch (error) {
      console.error('Erreur auth:', error);
      setError(error.response?.data?.error || t('auth.errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const features = [
    {
      icon: '🕸️',
      title: t('auth.features.graphModeling.title'),
      description: t('auth.features.graphModeling.description')
    },
    {
      icon: '🤖',
      title: t('auth.features.aiIntegrated.title'),
      description: t('auth.features.aiIntegrated.description')
    },
    {
      icon: '🔍',
      title: t('auth.features.vectorSearch.title'),
      description: t('auth.features.vectorSearch.description')
    },
    {
      icon: '⚡',
      title: t('auth.features.automaticTests.title'),
      description: t('auth.features.automaticTests.description')
    }
  ];

  return (
    <div className="auth-app-split">
      {/* Language Selector en haut à gauche */}
      <div className="auth-language-selector-top">
        <LanguageSelector variant="compact" showFlag={true} showText={true} />
      </div>
      
      {/* Partie gauche - Connexion/Inscription */}
      <div className="auth-left-panel">
        <div className="auth-container">
          {/* Header */}
          <div className="auth-header">
            <img src={logo} alt="Nodemented Logo" className="auth-logo" />
            <h1>{t('authApp.title')}</h1>
            <p>{t('authApp.subtitle')}</p>
          </div>

          {/* Switcher Mode */}
          <div className="auth-switcher">
            <button
              className={mode === 'login' ? 'active' : ''}
              onClick={() => setMode('login')}
            >
              {t('authApp.login')}
            </button>
            <button
              className={mode === 'register' ? 'active' : ''}
              onClick={() => setMode('register')}
            >
              {t('authApp.register')}
            </button>
          </div>

          {/* Formulaire */}
          <form onSubmit={handleSubmit} className="auth-form">
            {mode === 'register' && (
              <>
                {/* Type de compte */}
                <div className="form-group">
                  <label>{t('authApp.accountType.label')}</label>
                  <div className="radio-group">
                    <label>
                      <input
                        type="radio"
                        value="user"
                        checked={formData.accountType === 'user'}
                        onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
                      />
                      {t('authApp.accountType.user')}
                    </label>
                   {/* <label>
                      <input
                        type="radio"
                        value="org-admin"
                        checked={formData.accountType === 'org-admin'}
                        onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
                      />
                      {t('authApp.accountType.orgAdmin')}
                    </label>*/}
                  </div>
                </div>

                {/* Nom/Prénom */}
                <div className="form-row">
                  <div className="form-group">
                    <label>{t('authApp.firstName')}</label>
                    <input
                      type="text"
                      value={formData.firstName}
                      onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>{t('authApp.lastName')}</label>
                    <input
                      type="text"
                      value={formData.lastName}
                      onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                      required
                    />
                  </div>
                </div>

                {/* Organisation (si admin) */}
                {formData.accountType === 'org-admin' && (
                  <div className="form-group">
                    <label>{t('authApp.organization')}</label>
                    <input
                      type="text"
                      value={formData.organizationName}
                      onChange={(e) => setFormData({ ...formData, organizationName: e.target.value })}
                      placeholder={t('placeholders.organizationName')}
                      required
                    />
                  </div>
                )}

                {/* Code d'invitation (optionnel) */}
                <div className="form-group">
                  <label>{t('authApp.inviteCode')}</label>
                  <input
                    type="text"
                    value={formData.inviteCode || ''}
                    onChange={(e) => setFormData({ ...formData, inviteCode: e.target.value })}
                    placeholder="XXXX-XXXX-XXXX-XXXX-XXXX-XXXX"
                    style={{ fontFamily: 'Monaco, "Cascadia Code", monospace', letterSpacing: '1px' }}
                  />
                  <small style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>
                    {t('authApp.inviteCodeHint')}
                  </small>
                </div>
              </>
            )}

            {/* Email */}
            <div className="form-group">
              <label>{t('authApp.email')}</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
              />
            </div>

            {/* Mot de passe */}
            <div className="form-group">
              <label>{t('authApp.password')}</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
              />
              {mode === 'register' && (
                <small style={{ color: 'red', fontSize: '0.875rem', marginTop: '4px', display: 'block' }}>
                  {t('authApp.passwordHint')}
                </small>
              )}
            </div>

            {/* Confirmer mot de passe (inscription) */}
            {mode === 'register' && (
              <div className="form-group">
                <label>{t('authApp.confirmPassword')}</label>
                <input
                  type="password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                  required
                />
              </div>
            )}

            {/* Erreur */}
            {error && (
              <div className="error-message">
                ❌ {error}
              </div>
            )}

            {/* Bouton Submit */}
            <button type="submit" disabled={loading} className="auth-submit">
              {loading ? '🔄 ' + t('app.loading') : (mode === 'login' ? '🚀 ' + t('authApp.login') : '✨ ' + t('authApp.register'))}
            </button>
          </form>
          {/* Vidéo de démonstration */}

          <div className="demo-video-compact">
            <div className="demo-video-placeholder">
              <iframe
                className="tutorial-video"
                src="https://www.youtube.com/embed/Qy92Dn3ln30?autoplay=0&controls=1"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{ width: '110%', height: '110%', borderRadius: '12px', boxShadow: '0 4px 24px rgba(59,130,246,0.10)' }}
              ></iframe>
            </div>
          </div>
        </div>
      </div>

      {/* Partie droite - Démonstration */}
      <div className="auth-right-panel">
        <div className="demo-content">
          {/* Header démo */}
          <div className="demo-showcase-header">
            <h2>🚀 {t('authApp.discoverTitle')}</h2>
            <p>{t('authApp.discoverSubtitle')}</p>
          </div>



          {/* Fonctionnalités principales en compact */}
          <div className="features-compact">
            <h3>💡 {t('authApp.featuresTitle')}</h3>
            <div className="features-list">
              {features.map((feature, index) => (
                <div key={index} className="feature-item-compact">
                  <span className="feature-icon-small">{feature.icon}</span>
                  <div className="feature-text">
                    <h4>{feature.title}</h4>
                    <p>{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cas d'usage compact */}
          <div className="use-cases-compact">
            <h3>🎯 {t('authApp.useCasesTitle')}</h3>
            <div className="use-cases-list">
              <div className="use-case-compact">
                <span className="use-case-icon">👥</span>
                <div>
                  <h4>{t('authApp.useCases.productTeams.title')}</h4>
                  <p>{t('authApp.useCases.productTeams.description')}</p>
                </div>
              </div>
              <div className="use-case-compact">
                <span className="use-case-icon">🏗️</span>
                <div>
                  <h4>{t('authApp.useCases.softwareArchitects.title')}</h4>
                  <p>{t('authApp.useCases.softwareArchitects.description')}</p>
                </div>
              </div>
              <div className="use-case-compact">
                <span className="use-case-icon">🧪</span>
                <div>
                  <h4>{t('authApp.useCases.qualityEngineers.title')}</h4>
                  <p>{t('authApp.useCases.qualityEngineers.description')}</p>
                </div>
              </div>
              <div className="use-case-compact">
                <span className="use-case-icon">📚</span>
                <div>
                  <h4>{t('authApp.useCases.knowledgeManagement.title')}</h4>
                  <p>{t('authApp.useCases.knowledgeManagement.description')}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Témoignage compact */}
          <div className="testimonial-compact">
            <div className="testimonial-content">
              💬 "{t('authApp.testimonial')}
            </div>
            <div className="testimonial-author">
              <img src="https://ui-avatars.com/api/?name=Marie+Dubois&background=667eea&color=fff" alt="Marie" />
              <div>
                <strong>Marie Dubois</strong>
                <span>Lead Developer, TechCorp</span>
              </div>
            </div>
          </div>

          {/* Stats en bas */}
          {/* <div className="demo-stats">
            <div className="stat">
              <span className="stat-number">500+</span>
              <span className="stat-label">Utilisateurs</span>
            </div>
            <div className="stat">
              <span className="stat-number">10k+</span>
              <span className="stat-label">Nœuds créés</span>
            </div>
            <div className="stat">
              <span className="stat-number">99.9%</span>
              <span className="stat-label">Uptime</span>
            </div>
          </div>*/}

          {/* Footer légal */}
          <div style={{
            marginTop: '3rem',
            paddingTop: '1.5rem',
            borderTop: '1px solid rgba(255,255,255,0.1)',
            textAlign: 'center',
            fontSize: '0.85rem',
            color: 'rgba(255,255,255,0.7)'
          }}>
            <div style={{ marginBottom: '0.5rem' }}>
              <a href="/terms" style={{ color: 'rgba(255,255,255,0.9)', textDecoration: 'none', margin: '0 1rem' }}>
                {t('footer.terms', 'Conditions Générales')}
              </a>
              <span style={{ color: 'rgba(255,255,255,0.3)' }}>•</span>
              <a href="/policy/cookies" style={{ color: 'rgba(255,255,255,0.9)', textDecoration: 'none', margin: '0 1rem' }}>
                {t('footer.cookies', 'Politique de Cookies')}
              </a>
              <span style={{ color: 'rgba(255,255,255,0.3)' }}>•</span>
              <a href="mailto:nodemented.contact@gmail.com" style={{ color: 'rgba(255,255,255,0.9)', textDecoration: 'none', margin: '0 1rem' }}>
                {t('footer.contact', 'Contact')}
              </a>
            </div>
            <div style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)' }}>
              © 2026 Nodemented. {t('footer.rights', 'Tous droits réservés')}.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthApp;